import { RouteComponentProps } from 'react-router';
import * as React from 'react';
import { ReactNode, RefObject } from 'react';
import { Context } from 'vm';
import {
  ActionStore,
  ModalStore,
  PublicStore,
  RouterStore,
  ToolStore,
  WaitQueueStore,
} from '../stores';
import { InfinityRetrieve } from '../models/common';
import { CalendarLayoutDataModel } from '../models/component';
import { CalendarItem } from '../utils/calendar/Calendar';
import { TabModel } from '../models';

export interface PageProps extends RouteComponentProps<any> {
  routerStore: RouterStore;
  publicStore: PublicStore;
  actionStore: ActionStore;
  modalStore: ModalStore;
  waitQueueStore: WaitQueueStore;
  onMount?: (page: any) => void;
  state?: any;
  cacheLifecycles?: Context;
}

export interface ExternalPageProps extends RouteComponentProps<any> {
  routerStore: RouterStore;
  publicStore: PublicStore;
  toolStore: ToolStore;
}

export interface PageToolEvents {
  onFirstOpenEvent?: () => void;
  onReopenEvent?: () => void;
  onRetrieveEvent?: () => void;
  onNewEvent?: () => void;
  onSaveEvent?: () => void;
  onDeleteEvent?: () => void;
  onPrintEvent?: () => void;
  onExcelEvent?: () => void;
  onMessageEvent?: (topic: string, message: string) => any;
  state?: any;
  setState(
    state: any,
    callback?: () => void
  ): void;
}

/**
 * 기본 레이아웃 속성
 * @interface
 */
export interface LayoutProps {
  refs?: RefObject<HTMLDivElement>;
  /**
   * 에이전트가 이 입력칸을 찾는 식별자. 렌더 결과에 data-el-field 로 나간다.
   * 템플릿의 값은 코드모드가 kb/catalog.json 을 근거로 주입한다 (Phase 2 Task 4).
   */
  elField?: string;
  /**
   * 선택지가 여럿인 위젯(OptionBox)에서 개별 항목을 가리키는 값.
   * 렌더 결과에 data-el-option 으로 나간다. 컨테이너가 elField 를, 항목이 이것을 단다.
   */
  elOption?: string;
  /**
   * 표의 한 칸이 어느 컬럼인가. 렌더 결과에 data-el-cell 로 나간다.
   * 표 안의 입력칸은 화면 필드가 아니라 행·열 좌표로만 지목할 수 있다.
   */
  elCell?: string;
  /** 표의 몇 번째 행인가. 렌더 결과에 data-el-row 로 나간다. */
  elRow?: string;
  /**
   * 이 표가 화면 상태의 어느 배열인가. 렌더 결과에 data-el-grid 로 나간다.
   * 한 화면에 표가 여럿이면 행을 더하는 단추도 여럿이라, 표를 알아야 짝을 찾는다.
   */
  elGrid?: string;
  role?: any;
  className?: any;
  style?: any;
  children?: any;
  weight?: number;
  size?: number;
  minHeight?: number;
  tabindex?: number;
  isScroll?: boolean;
  onClick?: () => any;
  onTouchStart?: () => any;
  onDoubleClick?: () => any;
  onMouseMove?: (e: React.MouseEvent<HTMLDivElement>) => any;
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => any;
  onWheel?: (e: React.WheelEvent<HTMLDivElement>) => any;
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => any;
  onUnFocus?: (e: React.FocusEvent<HTMLDivElement>) => any;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => any;
}

export interface TemplateProps<T> {
  scope: T;
  update?: (newStates: any, callback?: () => void) => void;
  dataUpdate?: (newStates: any, callback?: () => void) => void;
  requestRowFocusEvent?: () => void;
}

/**
 * 동적 레이아웃
 * @interface
 */
export interface FlexLayoutProps extends LayoutProps {
  /**
   * 세로방향으로 뷰 쌓기
   */
  isVertical?: boolean;
  disabled?: boolean;
  disabledText?: string;
  ellipsis?: number;
  highlight?: boolean;
  align?: string;
  justify?: string;
}

export type GLHF<T = any, C = any> = (scope: C) => Array<GridLayoutHeader<T, C>>;

export interface GridLayoutHeader<T = any, C = any> {
  id: string;
  text: string | React.ReactNode;
  editable?: boolean;
  isHidden?: boolean;
  width?: number; // percent
  group?: boolean;
  color?: string;
  value?: (item: any, scope?: C) => any; // 가상의 헤더를 만들때 반영되는 값
  sum?: (item: any, data: Array<any>, scope?: C) => React.ReactNode; // 소계
  trail?: (data: Array<any>, scope?: C) => React.ReactNode; // 최 하단 총계
  render?: (
    item: T,
    scope?: C,
    index?: number,
  ) => React.ReactElement;
}

export interface GridLayoutProps<T = {}> extends LayoutProps {
  scope?: T;
  contentWidth?: number;
  header: Array<GridLayoutHeader>;
  headerHeight?: number;
  rowHeight: number;
  columnLockIndex?: number;
  data: any;
  infinityHandler?: InfinityRetrieve;
  rowColor?: (item: T) => string;
  onRowClick?: (item: any, index: number) => void;
  onRowDoubleClick?: (item: any, index: number) => void;
  onHorizontalScroll?: (posX: number) => void;
  publicStore?: PublicStore;
  denyWhenNewRow?: boolean;

  /**
   * * 무한 스크롤바
   * onBottom() 이벤트를 발생시킬지
   */
  hasNext?: boolean;

  /**
   * * 무한 스크롤바
   * 스크롤이 바닥까지 내려왔을 때
   */
  onBottom?: () => void;
}

export interface GridLayoutAdditionalHeaderProps {
  className?: any;
  style?: any;
  height?: number;
  contentWidth?: number;
  children?: any;
}

export type TLHF<T = any, C = any> = (scope: C) => Array<TableLayoutHeader<T, C>>;

export type RowUpdate<T = any> = <K extends keyof T>(changed: (Pick<T, K> | T | null)) => void;

export interface TableLayoutHeader<T = any, C = any> {
  id: string;
  text: string | React.ReactNode;
  sort?: string;
  color?: string;
  editable?: boolean;
  isHidden?: boolean;
  width?: number; // px
  group?: boolean;
  trail?: (data: Array<any>, scope?: C) => React.ReactNode; // 최 하단 총계
  render?: (
    item: T,
    rowUpdate: RowUpdate<T>,
    ref?: React.RefObject<any>,
    scope?: C,
    index?: number,
  ) => React.ReactElement;
}

export interface TableLayoutProps<T = {}> extends LayoutProps {
  publicStore?: PublicStore;
  scope?: T;

  header: Array<TableLayoutHeader>;
  headerHeight?: number;
  rowHeight: number;
  data: any;
  newOnlyOne?: boolean;
  infinityHandler?: InfinityRetrieve;
  rowColor?: (item: T) => string;
  isPaging?: boolean;

  onRowFocusEvent?: (item: any, index: number) => void;
  onRowDoubleClick?: (item: any, index: number) => void;
  onVerticalScroll?: (posX: number) => void;
  onHorizontalScroll?: (posX: number) => void;

  /**
   * 행 변경시
   * @param rows 전체
   * @param updatedRows 변경된 행들만
   */
  onChange?: (rows: Array<any>, updatedRows: Array<any>) => void;

  /**
   * 무한 스크롤바
   * - onBottom() 이벤트를 발생시킬지
   */
  hasNext?: boolean;

  /**
   * 무한 스크롤바
   * - 스크롤이 바닥까지 내려왔을 때
   */
  onBottom?: () => void;
}

export interface DragAndDropLayoutHeader<T = any, C = any> {
  id: string;
  text: string | React.ReactNode;
  sort?: string;
  color?: string;
  editable?: boolean;
  isHidden?: boolean;
  width?: number; // px
  render?: (
    item: T,
    rowUpdate: <K extends keyof T>(changed: (Pick<T, K> | T | null)) => void,
    ref: React.RefObject<any>,
    scope?: C,
  ) => React.ReactElement;
}

/**
 * 드래그 앤 드랍 레이아웃
 * 테이블 레이아웃과 비슷합니다
 * @interface
 */
export interface DragAndDropLayoutProps<T = {}> extends LayoutProps {
  publicStore?: PublicStore;
  scope?: T;

  header: Array<TableLayoutHeader>;
  headerHeight?: number;
  rowHeight: number;
  data: any;
  newOnlyOne?: boolean;
  infinityHandler?: InfinityRetrieve;

  onRowFocusEvent?: (item: any, index: number) => void;

  /**
   * 행 변경시
   * @param rows 전체
   * @param updatedRows 변경된 행들만
   */
  onChange?: (rows: Array<any>, updatedRows: Array<any>) => void;
}

/**
 * 모달 뷰
 * @interface
 */
export interface ModalViewProps {
  width?: number;
  height?: number;
  style?: any;
  isVisible?: boolean;
  onClose?: () => void;
  zIndex?: number;
}

/**
 * 달력 레이아웃
 *
 * 컴포넌트에 해당월의 전체 데이터를 미리 넣어두고
 * 렌더링할때 그날의 데이터 배열을 이용합니다.
 *
 * @interface
 */
export interface CalendarLayoutProps<T> {
  actionStore?: ActionStore;
  className?: any;
  weight?: number;
  size?: number;
  style?: number;
  onDateClick?: (date: CalendarItem, items: Array<CalendarLayoutDataModel<T>>) => any;
  onDateDoubleClick?: (date: CalendarItem, items: Array<CalendarLayoutDataModel<T>>) => any;

  month: string; // 6자리 월 (ex: 202011)
  data: Array<CalendarLayoutDataModel<T>>; // 해당 월의 전체 데이터
  render: (
    date: CalendarItem, // 렌더링하는 날에 대한 정보
    items: Array<CalendarLayoutDataModel<T>>, // 렌더링 할때는 해당 일자의 데이터 배열이 주어집니다
  ) => React.ReactElement;
}

export interface TabLayoutDataItem<T = any> {
  tab: TabModel;
  template: (scope: T) => ReactNode;
}
